import { c as _c } from "react/compiler-runtime";
import type { ChangelogPost } from '@onefootprint/changelog';
import { DOCS_BASE_URL } from '@onefootprint/dev-tools';
import { IcoArrowTopRight16, IcoChevronRight16, IcoLogOut16 } from '@onefootprint/icons';
import type { Organization } from '@onefootprint/request-types/dashboard';
import { Dropdown } from '@onefootprint/ui';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTheme } from 'next-themes';
import { useQueryState } from 'nuqs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RSG_SHORTCUT_KEYS } from '@/config/constants';
import type { UserSession } from '@/hooks/use-session';
import WhatsNewDialog from '../whats-new-dialog';
import TenantsList from './components/tenants-list';
import UserName from './components/user-name/user-name';
const PgpUploadTool = dynamic(() => import('./components/pgp-upload-tool'), {
  ssr: false
});
export type NavDropdownProps = {
  tenants: Organization[];
  currTenantId: string;
  assumedTenant?: {
    id: string;
    name: string;
  };
  onAssumeTenant: (tenantId: string) => void;
  isSwitchingTenant?: boolean;
  posts: ChangelogPost[];
  user: UserSession;
  children: React.ReactNode;
  triggerFullWidth?: boolean;
};
const NavDropdown = t0 => {
  const $ = _c(122);
  const {
    tenants,
    currTenantId,
    assumedTenant,
    onAssumeTenant,
    isSwitchingTenant,
    user,
    posts,
    children,
    triggerFullWidth
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "components.privateLayout.nav"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("common", t1);
  const router = useRouter();
  const {
    theme,
    setTheme
  } = useTheme();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      history: "push"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [pgpHelper, setPgpHelper] = useQueryState("pgp-tool", t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      history: "push"
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [whatsNew, setWhatsNew] = useQueryState("whats-new", t3);
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      history: "push"
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const [, setRsg] = useQueryState("rsg", t4);
  let t5;
  if ($[4] !== setRsg) {
    t5 = () => {
      setIsDropdownOpen(false);
      setRsg("open");
    };
    $[4] = setRsg;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const handleRsgOpen = t5;
  let t6;
  if ($[6] !== router) {
    t6 = () => {
      router.push("/logout");
    };
    $[6] = router;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const handleLogout = t6;
  let t7;
  if ($[8] !== t) {
    t7 = t(_temp);
    $[8] = t;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t7) {
    t8 = {
      value: "light",
      label: t7
    };
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t) {
    t9 = t(_temp2);
    $[12] = t;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t9) {
    t10 = {
      value: "dark",
      label: t9
    };
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t) {
    t11 = t(_temp3);
    $[16] = t;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t11) {
    t12 = {
      value: "system",
      label: t11
    };
    $[18] = t11;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== t10 || $[21] !== t12 || $[22] !== t8) {
    t13 = [t8, t10, t12];
    $[20] = t10;
    $[21] = t12;
    $[22] = t8;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  const themeOptions = t13;
  let t14;
  if ($[24] !== theme || $[25] !== themeOptions) {
    t14 = themeOptions.find(option => option.value === theme);
    $[24] = theme;
    $[25] = themeOptions;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  const currentTheme = t14;
  const t15 = triggerFullWidth ? "100%" : undefined;
  let t16;
  if ($[27] !== children || $[28] !== t15) {
    t16 = <Dropdown.Trigger asChild={true} width={t15}>{children}</Dropdown.Trigger>;
    $[27] = children;
    $[28] = t15;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      minWidth: "14rem"
    };
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== user.email || $[32] !== user.firstName || $[33] !== user.lastName) {
    t18 = <UserName name={user.firstName} lastName={user.lastName} email={user.email} />;
    $[31] = user.email;
    $[32] = user.firstName;
    $[33] = user.lastName;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <Dropdown.Divider />;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== assumedTenant || $[37] !== currTenantId || $[38] !== isSwitchingTenant || $[39] !== onAssumeTenant || $[40] !== tenants) {
    t20 = tenants?.length > 1 || assumedTenant ? <TenantsList tenants={tenants} currTenantId={currTenantId} assumedTenant={assumedTenant} isSwitchingTenant={isSwitchingTenant} onSelect={tenantId => {
      onAssumeTenant(tenantId);
      setIsDropdownOpen(false);
    }} /> : null;
    $[36] = assumedTenant;
    $[37] = currTenantId;
    $[38] = isSwitchingTenant;
    $[39] = onAssumeTenant;
    $[40] = tenants;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <Dropdown.Divider />;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== t) {
    t22 = t(_temp4);
    $[43] = t;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] !== t22) {
    t23 = <Dropdown.Item iconRight={IcoArrowTopRight16} asLink={true} asChild={true} className="text-body-3 !px-2 !h-8 !gap-2"><Link href={`${DOCS_BASE_URL}/login?redirectUrl=/`} target="_blank">{t22}</Link></Dropdown.Item>;
    $[45] = t22;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  let t24;
  if ($[47] !== t) {
    t24 = t(_temp5);
    $[47] = t;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  let t25;
  if ($[49] !== t24) {
    t25 = <Dropdown.Item iconRight={IcoArrowTopRight16} asLink={true} asChild={true} className="text-body-3 !px-2 !h-8 !gap-2"><Link href={`${DOCS_BASE_URL}/login?redirectUrl=/api-reference`} target="_blank">{t24}</Link></Dropdown.Item>;
    $[49] = t24;
    $[50] = t25;
  } else {
    t25 = $[50];
  }
  let t26;
  if ($[51] !== t) {
    t26 = t(_temp6);
    $[51] = t;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  let t27;
  if ($[53] !== handleRsgOpen || $[54] !== t26) {
    t27 = <Dropdown.Item onSelect={handleRsgOpen} className="justify-between text-body-3 !px-2 !h-8 !gap-2" shortcutHint={RSG_SHORTCUT_KEYS}>{t26}</Dropdown.Item>;
    $[53] = handleRsgOpen;
    $[54] = t26;
    $[55] = t27;
  } else {
    t27 = $[55];
  }
  let t28;
  if ($[56] !== setPgpHelper) {
    t28 = () => {
      setIsDropdownOpen(false);
      setPgpHelper("open");
    };
    $[56] = setPgpHelper;
    $[57] = t28;
  } else {
    t28 = $[57];
  }
  let t29;
  if ($[58] !== t) {
    t29 = t(_temp7);
    $[58] = t;
    $[59] = t29;
  } else {
    t29 = $[59];
  }
  let t30;
  if ($[60] !== t28 || $[61] !== t29) {
    t30 = <Dropdown.Item onSelect={t28} className="text-body-3 !px-2 !h-8 !gap-2">{t29}</Dropdown.Item>;
    $[60] = t28;
    $[61] = t29;
    $[62] = t30;
  } else {
    t30 = $[62];
  }
  let t31;
  if ($[63] !== setWhatsNew) {
    t31 = () => {
      setIsDropdownOpen(false);
      setWhatsNew("open");
    };
    $[63] = setWhatsNew;
    $[64] = t31;
  } else {
    t31 = $[64];
  }
  let t32;
  if ($[65] !== t) {
    t32 = t(_temp8);
    $[65] = t;
    $[66] = t32;
  } else {
    t32 = $[66];
  }
  let t33;
  if ($[67] !== t31 || $[68] !== t32) {
    t33 = <Dropdown.Item onSelect={t31} className="text-body-3 !px-2 !h-8 !gap-2">{t32}</Dropdown.Item>;
    $[67] = t31;
    $[68] = t32;
    $[69] = t33;
  } else {
    t33 = $[69];
  }
  let t34;
  if ($[70] !== t23 || $[71] !== t25 || $[72] !== t27 || $[73] !== t30 || $[74] !== t33) {
    t34 = <Dropdown.Group className="p-1 flex flex-col gap-0.5">{t23}{t25}{t27}{t30}{t33}</Dropdown.Group>;
    $[70] = t23;
    $[71] = t25;
    $[72] = t27;
    $[73] = t30;
    $[74] = t33;
    $[75] = t34;
  } else {
    t34 = $[75];
  }
  let t35;
  if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <Dropdown.Divider />;
    $[76] = t35;
  } else {
    t35 = $[76];
  }
  let t36;
  if ($[77] !== t) {
    t36 = t(_temp9);
    $[77] = t;
    $[78] = t36;
  } else {
    t36 = $[78];
  }
  const t37 = currentTheme?.label;
  let t38;
  if ($[79] !== t37) {
    t38 = <span className="ml-auto text-tertiary">{t37}</span>;
    $[79] = t37;
    $[80] = t38;
  } else {
    t38 = $[80];
  }
  let t39;
  if ($[81] !== t36 || $[82] !== t38) {
    t39 = <Dropdown.SubTrigger iconRight={IcoChevronRight16} className="text-body-3 !px-2 !h-8 !gap-2">{t36}{t38}</Dropdown.SubTrigger>;
    $[81] = t36;
    $[82] = t38;
    $[83] = t39;
  } else {
    t39 = $[83];
  }
  const t40 = theme ?? "";
  let t41;
  if ($[84] !== themeOptions) {
    t41 = themeOptions.map(_temp10);
    $[84] = themeOptions;
    $[85] = t41;
  } else {
    t41 = $[85];
  }
  let t42;
  if ($[86] !== setTheme || $[87] !== t40 || $[88] !== t41) {
    t42 = <Dropdown.Portal><Dropdown.SubContent sideOffset={6} className="p-1"><Dropdown.RadioGroup value={t40} onValueChange={setTheme} className="p-0">{t41}</Dropdown.RadioGroup></Dropdown.SubContent></Dropdown.Portal>;
    $[86] = setTheme;
    $[87] = t40;
    $[88] = t41;
    $[89] = t42;
  } else {
    t42 = $[89];
  }
  let t43;
  if ($[90] !== t39 || $[91] !== t42) {
    t43 = <Dropdown.Group className="p-1 flex flex-col gap-0.5"><Dropdown.Sub>{t39}{t42}</Dropdown.Sub></Dropdown.Group>;
    $[90] = t39;
    $[91] = t42;
    $[92] = t43;
  } else {
    t43 = $[92];
  }
  let t44;
  if ($[93] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <Dropdown.Divider />;
    $[93] = t44;
  } else {
    t44 = $[93];
  }
  let t45;
  if ($[94] !== t) {
    t45 = t(_temp11);
    $[94] = t;
    $[95] = t45;
  } else {
    t45 = $[95];
  }
  let t46;
  if ($[96] !== handleLogout || $[97] !== t45) {
    t46 = <Dropdown.Group className="p-1 flex flex-col gap-0.5"><Dropdown.Item onSelect={handleLogout} iconLeft={IcoLogOut16} destructive={true} className="text-body-3 !px-2 !h-8 !gap-2">{t45}</Dropdown.Item></Dropdown.Group>;
    $[96] = handleLogout;
    $[97] = t45;
    $[98] = t46;
  } else {
    t46 = $[98];
  }
  let t47;
  if ($[99] !== t18 || $[100] !== t20 || $[101] !== t34 || $[102] !== t43 || $[103] !== t46) {
    t47 = <Dropdown.Portal><Dropdown.Content style={t17} className="w-[var(--radix-dropdown-menu-trigger-width)]" align="start" side="top" sideOffset={8}>{t18}{t19}{t20}{t21}{t34}{t35}{t43}{t44}{t46}</Dropdown.Content></Dropdown.Portal>;
    $[99] = t18;
    $[100] = t20;
    $[101] = t34;
    $[102] = t43;
    $[103] = t46;
    $[104] = t47;
  } else {
    t47 = $[104];
  }
  let t48;
  if ($[105] !== isDropdownOpen || $[106] !== t16 || $[107] !== t47) {
    t48 = <Dropdown.Root onOpenChange={setIsDropdownOpen} open={isDropdownOpen}>{t16}{t47}</Dropdown.Root>;
    $[105] = isDropdownOpen;
    $[106] = t16;
    $[107] = t47;
    $[108] = t48;
  } else {
    t48 = $[108];
  }
  let t49;
  if ($[109] !== pgpHelper || $[110] !== setPgpHelper) {
    t49 = pgpHelper === "open" && <PgpUploadTool open={true} onClose={() => {
      setIsDropdownOpen(false);
      setPgpHelper(null);
    }} />;
    $[109] = pgpHelper;
    $[110] = setPgpHelper;
    $[111] = t49;
  } else {
    t49 = $[111];
  }
  const t50 = whatsNew === "open";
  let t51;
  if ($[112] !== setWhatsNew) {
    t51 = () => {
      setIsDropdownOpen(false);
      setWhatsNew(null);
    };
    $[112] = setWhatsNew;
    $[113] = t51;
  } else {
    t51 = $[113];
  }
  let t52;
  if ($[114] !== posts || $[115] !== t50 || $[116] !== t51) {
    t52 = <WhatsNewDialog open={t50} onClose={t51} posts={posts} />;
    $[114] = posts;
    $[115] = t50;
    $[116] = t51;
    $[117] = t52;
  } else {
    t52 = $[117];
  }
  let t53;
  if ($[118] !== t48 || $[119] !== t49 || $[120] !== t52) {
    t53 = <>{t48}{t49}{t52}</>;
    $[118] = t48;
    $[119] = t49;
    $[120] = t52;
    $[121] = t53;
  } else {
    t53 = $[121];
  }
  return t53;
};
export default NavDropdown;
function _temp($) {
  return $.theme.light;
}
function _temp2($_0) {
  return $_0.theme.dark;
}
function _temp3($_1) {
  return $_1.theme.system;
}
function _temp4($_2) {
  return $_2.helpLinks.documentation;
}
function _temp5($_3) {
  return $_3.helpLinks.apiReference;
}
function _temp6($_4) {
  return $_4.helpLinks.riskSignalsGlossary;
}
function _temp7($_5) {
  return $_5.helpLinks.pgpHelperTool;
}
function _temp8($_6) {
  return $_6.whatsNew.title;
}
function _temp9($_7) {
  return $_7.theme.label;
}
function _temp10(option_0) {
  return <Dropdown.Item key={option_0.value} variant="radio" value={option_0.value} className="text-body-3 !px-2 !h-8 !gap-2">{option_0.label}</Dropdown.Item>;
}
function _temp11($_8) {
  return $_8.logOut;
}